import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import type { CustomerLoginPayload } from '@vs-shop/schemas/customer';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/customers/token';
const HTTP_METHOD = 'post';

export type MakeCreateTokenForCustomerDeps<CustomerTokenReadSchema extends Schema.Top> = {
  readonly customerTokenRead: CustomerTokenReadSchema;
};

export const makeCreateTokenForCustomer = <CustomerTokenReadSchema extends Schema.Top>(
  deps: MakeCreateTokenForCustomerDeps<CustomerTokenReadSchema>
) => {
  const CreateCustomerTokenError = createBackendApiError({
    domain: 'CustomerToken',
    endpoint: 'Create',
    path: ENDPOINT,
    identifierField: 'email',
  });

  const postCustomerTokenFetch = (data: CustomerLoginPayload) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/json', 'application/json')
        .fetch({ body: data });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'customer.email': data.email,
        },
      })
    );

  const createCustomerToken = (data: CustomerLoginPayload) =>
    postCustomerTokenFetch(data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.customerTokenRead)(response),
          404: (response) =>
            Effect.fail(
              new CreateCustomerTokenError.NotFoundError({ email: data.email, response })
            ),
          422: CreateCustomerTokenError.createViolationError(data.email),
          orElse: (response) =>
            Effect.fail(
              new CreateCustomerTokenError.ApiRequestError({ email: data.email, response })
            ),
        })
      ),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.CustomerTokenCreate,
          'Debug',
          `Successfully created customer token for email: ${data.email ?? 'unknown'}`
        )
      ),
      Effect.withSpan('api.customer.createToken', {
        kind: 'internal',
        attributes: {
          'customer.email': data.email,
        },
      })
    );

  return { CreateCustomerTokenError, createCustomerToken };
};
